/**
 * Canonical Game Creation Types across the platform.
 */
export type CanonicalCreationType =
  | 'bundled'
  | 'upload'
  | 'gamepix'
  | 'gamemonetize'
  | 'gamedistribution'
  | 'json_import'
  | 'manual'
  | 'ai'
  | 'import';

export interface CreationTypeMeta {
  key: CanonicalCreationType;
  label: string;
  color: string;
  description?: string;
}

export const CREATION_TYPES: Record<CanonicalCreationType, CreationTypeMeta> = {
  bundled: {
    key: 'bundled',
    label: 'Bundled',
    color: 'green',
    description: 'Pre-installed bundled games',
  },
  upload: {
    key: 'upload',
    label: 'Upload ZIP',
    color: 'cyan',
    description: 'Manually uploaded games (ZIP / HTML5)',
  },
  manual: {
    key: 'manual',
    label: 'Manual',
    color: 'blue',
    description: 'Created manually by form',
  },
  json_import: {
    key: 'json_import',
    label: 'JSON Batch',
    color: 'geekblue',
    description: 'Batch imported via JSON file',
  },
  gamepix: {
    key: 'gamepix',
    label: 'GamePix',
    color: 'purple',
    description: 'Imported from GamePix provider',
  },
  gamemonetize: {
    key: 'gamemonetize',
    label: 'GameMonetize',
    color: 'orange',
    description: 'Imported from GameMonetize provider',
  },
  gamedistribution: {
    key: 'gamedistribution',
    label: 'GameDistribution',
    color: 'volcano',
    description: 'Imported from GameDistribution provider',
  },
  ai: {
    key: 'ai',
    label: 'AI Generated',
    color: 'magenta',
    description: 'Created by AI',
  },
  import: {
    key: 'import',
    label: 'Import',
    color: 'blue',
    description: 'Generic imported games',
  },
};

/**
 * Normalizes legacy, raw, or variant creationType strings to a canonical creation type.
 */
export function normalizeCreationType(type?: string | null): CanonicalCreationType | string {
  if (!type) return 'upload';
  const clean = type.toLowerCase().replace(/[\s_-]+/g, '');

  if (clean === 'bundled' || clean === 'defaultbundled') {
    return 'bundled';
  }
  if (clean === 'gamepix' || clean === 'gamepiximport') {
    return 'gamepix';
  }
  if (clean === 'gamemonetize' || clean === 'gamemonetizeimport') {
    return 'gamemonetize';
  }
  if (clean === 'gamedistribution' || clean === 'gamedistributionimport') {
    return 'gamedistribution';
  }
  if (clean === 'jsonimport' || clean === 'batchjson' || clean === 'json') {
    return 'json_import';
  }
  if (clean === 'manual') {
    return 'manual';
  }
  if (clean === 'ai' || clean === 'aigenerated') {
    return 'ai';
  }
  if (clean === 'upload') {
    return 'upload';
  }
  if (clean === 'import') {
    return 'import';
  }
  return type;
}

/**
 * Returns label and tag color for any creationType (canonical or legacy).
 */
export function getCreationTypeMeta(type?: string | null): { label: string; color: string } {
  const canonical = normalizeCreationType(type);
  if (typeof canonical === 'string' && canonical in CREATION_TYPES) {
    return CREATION_TYPES[canonical as CanonicalCreationType];
  }
  return {
    label: type || 'N/A',
    color: 'default',
  };
}

/**
 * Filter options for Ant Design Select component
 */
export const CREATION_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'manual', label: 'Manual' },
  { value: 'upload', label: 'Upload ZIP' },
  { value: 'json_import', label: 'JSON Batch' },
  { value: 'bundled', label: 'Bundled' },
  { value: 'gamepix', label: 'GamePix' },
  { value: 'gamedistribution', label: 'GameDistribution' },
  { value: 'gamemonetize', label: 'GameMonetize' },
  { value: 'ai', label: 'AI Generated' },
  { value: 'import', label: 'Generic Import' },
];
